import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, SlidersHorizontal, Boxes, ArrowRight, ArrowLeft } from 'lucide-react';
import { listVehicles, SOURCE_LABELS, STATUS_LABELS } from '@/api/auction';
import { LotCard } from '@/components/auction/LotCard';
import { BuyerAuthDialog } from '@/components/auction/BuyerAuthDialog';
import { useBuyerAuth } from '@/context/BuyerAuthContext';
import { INPUT_CLS } from '@/components/brand/BrandKit';
import { at, SOURCE_OPTIONS_AR, STATUS_OPTIONS_AR } from '@/i18n/auctionI18n';

const FIELD = `${INPUT_CLS} border px-3`;

export default function AuctionCatalogPage({ lang = 'en' }) {
  const isAr = lang === 'ar';
  const { buyer, logout } = useBuyerAuth();
  const [vehicles, setVehicles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [authOpen, setAuthOpen] = useState(false);

  const sourceOpts = isAr ? SOURCE_OPTIONS_AR : SOURCE_LABELS;
  const statusOpts = isAr ? STATUS_OPTIONS_AR : STATUS_LABELS;
  const BackArrow = isAr ? ArrowRight : ArrowLeft;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (search.trim()) params.search = search.trim();
      if (source) params.source = source;
      if (status) params.availability_status = status;
      const d = await listVehicles(params);
      setVehicles(d.vehicles);
      setTotal(d.total);
    } catch { setVehicles([]); } finally { setLoading(false); }
  }, [search, source, status]);

  useEffect(() => { document.title = isAr ? 'كتالوج المزاد | يوروجلف للسيارات المستعملة' : 'Auction Catalogue | Eurogulf Used Cars'; }, [isAr]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="auction-catalog-page" dir={isAr ? 'rtl' : 'ltr'} className="bg-[#FAFAFA] min-h-screen">
      <section className="bg-[#121212] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <Link to={isAr ? '/ar/used-cars' : '/used-cars'} className="inline-flex items-center gap-1.5 font-body text-xs text-white/50 hover:text-white"><BackArrow className="w-3.5 h-3.5" /> {at(lang, 'backToUsedCars')}</Link>
            <h1 className="mt-2 font-heading font-black text-3xl sm:text-4xl tracking-tight">{at(lang, 'catalogTitle')}</h1>
            <p className="mt-2 font-body text-sm text-white/70">{at(lang, 'catalogSubtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            {buyer ? (
              <>
                <span className="font-body text-sm text-white/70 hidden sm:inline">{buyer.company}</span>
                <button type="button" data-testid="catalog-logout" onClick={logout} className="btn-outline !border-white/40 !text-white hover:!bg-white hover:!text-[#121212] !py-2">{at(lang, 'signOut')}</button>
              </>
            ) : (
              <button type="button" data-testid="catalog-register" onClick={() => setAuthOpen(true)} className="btn-primary !py-2">{at(lang, 'registerToBid')}</button>
            )}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-black/10 bg-white sticky top-16 lg:top-[76px] z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className={`absolute ${isAr ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]`} />
            <input data-testid="catalog-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={at(lang, 'searchPlaceholder')} className={`${FIELD} w-full ${isAr ? 'pr-10' : 'pl-10'}`} />
          </div>
          <div className="flex gap-3">
            <select data-testid="catalog-source" value={source} onChange={(e) => setSource(e.target.value)} className={`${FIELD} !w-auto`}>
              <option value="">{at(lang, 'allSources')}</option>
              {Object.entries(sourceOpts).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select data-testid="catalog-status" value={status} onChange={(e) => setStatus(e.target.value)} className={`${FIELD} !w-auto`}>
              <option value="">{at(lang, 'allStatuses')}</option>
              {Object.entries(statusOpts).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <p className="font-body text-sm text-[#666666] mb-6 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-[#EE5A01]" /> {loading ? at(lang, 'loadingLots') : at(lang, 'lotsAvailable', total)}</p>
        {!loading && vehicles.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-black/15 bg-white p-14 text-center">
            <Boxes className="w-10 h-10 text-[#C4C4BE] mx-auto" />
            <p className="mt-4 font-heading font-bold text-[#121212]">{at(lang, 'noLotsTitle')}</p>
            <p className="mt-1 font-body text-sm text-[#666666]">{at(lang, 'noLotsSub')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {vehicles.map((v) => <LotCard key={v.id} v={v} lang={lang} />)}
          </div>
        )}
      </section>

      <BuyerAuthDialog open={authOpen} onOpenChange={setAuthOpen} defaultMode="register" lang={lang} />
    </div>
  );
}
